import { cn } from '@civilgrid/ui/utils';
import { useEffect, useId } from 'react';
import { useSnapshot } from 'valtio';
import { FeatureAnnouncement } from '~/pages/common/FeatureAnnouncements/FeatureAnnouncement';
import ProtectedRouteLayout from '~/pages/common/ProtectedRouteLayout';
import rootStore from '~/store';
import { PageHeader, ProjectsMapList, ProjectsTable } from './components';
import { useViewMode } from './hooks';
import MapView from './Map';
import { homeMapStore, homeViewStore } from './store';

/**
 * Project landing page: Table, Map, or Split view.
 * Shared state (count, loading, table state) lives in homeViewStore.
 */
export default function HomeViewV2() {
  const titleId = useId();
  const { inOnboardingMode, disclaimerViewed } = useSnapshot(rootStore) as typeof rootStore;
  const { viewMode, setViewMode } = useViewMode();
  const { tableCount, mapCount, tableLoading, mapLoading } = useSnapshot(homeViewStore);
  const { inNewProjectMode } = useSnapshot(homeMapStore);

  // In onboarding, always show the map view.
  useEffect(() => {
    if (inOnboardingMode && viewMode !== 'map') {
      setViewMode('map');
    }
  }, [inOnboardingMode, viewMode, setViewMode]);

  // Clear new project mode when navigating away from /home.
  useEffect(() => {
    return () => {
      homeMapStore.setNewProjectMode(false);
    };
  }, []);

  const rowCount = viewMode === 'map' ? mapCount : tableCount;
  const headerLoading = viewMode === 'map' ? mapLoading : tableLoading;

  return (
    <ProtectedRouteLayout includeNewProjectButton includeOrgProjectSwitcher includeSearch>
      {disclaimerViewed && !inOnboardingMode && <FeatureAnnouncement />}
      <div className="flex h-full w-full flex-col py-3 pl-3" data-testid="home-page">
        <div className="shrink-0 space-y-3 pr-3">
          <PageHeader
            rowCount={rowCount}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            isLoading={headerLoading}
            viewSwitcherDisabled={inNewProjectMode}
            titleId={titleId}
          />
        </div>
        <div className="mt-2 min-h-0 flex-1">
          {(viewMode === 'split' || viewMode === 'map') && (
            <div className="-mx-1 flex h-full gap-3 overflow-x-auto px-1 pt-1" data-testid="home-map-view-row">
              {viewMode === 'split' ? (
                <aside aria-label="Projects" className="min-w-0 flex-1">
                  <ProjectsTable viewMode={viewMode} titleId={titleId} />
                </aside>
              ) : (
                <aside aria-label="Projects" className="h-full w-60 shrink-0">
                  <ProjectsMapList />
                </aside>
              )}
              {/* The map canvas is its own "Interactive map" region; this section wraps it with the map controls. */}
              <section
                aria-label={viewMode === 'split' ? 'Split view map' : 'Map view'}
                className={cn('flex min-h-0 flex-1', inNewProjectMode ? 'min-w-[820px]' : 'min-w-0')}
                data-testid="home-map-view-map-cell"
              >
                <MapView viewMode={viewMode} />
              </section>
            </div>
          )}
          {viewMode === 'table' && (
            <div className="h-full pr-3 pt-1">
              <ProjectsTable viewMode={viewMode} titleId={titleId} />
            </div>
          )}
        </div>
      </div>
    </ProtectedRouteLayout>
  );
}
